import {
  FIAT,
  ASSETS,
  SYMMETRAL_CURRENCIES,
  IFT_CURRENCIES,
  OMNI_SUPPORTED_CURRENCIES,
  IFT_OMNI_SUPPORTED_CURRENCIES
} from '@/utils/constants.js'
import tenant from '@/mixins/tenant.js'

export default {
  mixins: [tenant],
  data () {
    return {
      fiatCurrencies: FIAT,
      assets: ASSETS
    }
  },
  methods: {},
  computed: {
    hasMultipleAccounts () {
      return this.totalAccounts.length > 1
    },
    walletAddresses () {
      return this.$store.state.transactions.walletAddresses.items || []
    },
    totalHoldingsBalance () {
      return (
        this.$store.state.report.dashboardReports.summary
          .totalHoldingsBalance || 0
      )
    },
    totalBalance () {
      return (
        this.$store.state.report?.dashboardReports?.summary
          ?.totalInvestmentBalance || 0
      )
    },
    totalYield () {
      return (
        this.$store.state.report?.dashboardReports?.summary?.totalYield || 0
      )
    },
    apyValue () {
      return this.$store.state.report?.dashboardReports?.summary.averageApy || 0
    },
    allAccounts () {
      return this.$store.state.report?.dashboardReports?.balances || []
    },
    filteredDepositAccounts () {
      // this will filter the asset accounts and return accounts that are valid for deposits
      const fiatDetails = this.totalAccounts.filter((item) =>
        this.fiatCurrencies.some((el) => el.currency === item.currency)
      )
      const assetAccounts = this.totalAccounts.filter((item) =>
        this.assets.some((el) => el.currency === item.currency)
      )
      const filteredAssets = assetAccounts.filter((asset) => {
        return this.walletAddresses.some((wallet) => {
          return (
            asset.currency === wallet.asset &&
            wallet.reason === this.$transactionType.DEPOSIT
          )
        })
      })
      const accounts = [...fiatDetails, ...filteredAssets]
      const sortedAccounts = accounts.sort(
        (a, b) => b.currentBalance - a.currentBalance
      )
      return sortedAccounts
    },
    withdrawalFiatCurrencyOptions () {
      let currencyOptions = []

      if (this.tenantIsTma || this.tenantIsSymmetral) {
        currencyOptions = this.filteredDepositAccounts?.filter(
          (item) => item.currency === this.$CURRENCY.NGN
        )
      } else {
        const excludedCurrencies = [...ASSETS.map((asset) => asset.currency)] // Extract currencies from ASSETS
        currencyOptions = this.filteredDepositAccounts?.filter(
          (item) => !excludedCurrencies.includes(item.currency)
        )
      }

      return currencyOptions
    },
    filteredBalanceAccounts () {
      const fiatDetails = this.totalAccounts
        .filter((acc) => (this.tenantIsIFT ? acc.currency === 'GHS' : true))
        .filter((item) =>
          this.fiatCurrencies.some((el) => el.currency === item.currency)
        )
      const assetAccounts = this.totalAccounts.filter((item) =>
        this.assets.some((el) => el.currency === item.currency)
      )
      const filteredAssets = assetAccounts.filter((asset) => {
        return this.walletAddresses.some((wallet) => {
          return asset.currency === wallet.asset
        })
      })
      const accounts = [...fiatDetails, ...filteredAssets]
      const sortedAccounts = accounts.sort(
        (a, b) => b.currentBalance - a.currentBalance
      )
      return sortedAccounts
    },
    totalAccounts () {
      let details = []
      switch (true) {
        case this.tenantIsTma:
          details = this.allAccounts?.filter(
            (item) => item.currency === this.$CURRENCY.NGN
          )
          break
        case this.tenantIsTorus:
          details = this.allAccounts?.filter((item) =>
            this.fiatCurrencies.find((el) => el.currency === item.currency)
          )
          break
        case this.tenantIsSymmetral:
          details = this.allAccounts?.filter((item) =>
            SYMMETRAL_CURRENCIES.find((el) => el.currency === item.currency)
          )
          break
        case this.tenantIsIFT:
          details = this.allAccounts?.filter((item) =>
            IFT_CURRENCIES.find((el) => el.currency === item.currency)
          )
          break

        default:
          details = this.allAccounts
      }
      return details
    },
    accountBalance () {
      return this.tenantIsTma || this.tenantIsSymmetral
        ? this.NGNBalance
        : this.USDBalance
    },
    balanceCurrency () {
      return this.tenantIsTma || this.tenantIsSymmetral ? 'NGN' : 'USD'
    },
    USDBalanceAccount () {
      return this.totalAccounts.find(
        (balance) => balance.currency === this.$CURRENCY.USD
      )
    },
    NGNBalanceAccount () {
      return this.totalAccounts.find(
        (balance) => balance.currency === this.$CURRENCY.NGN
      )
    },
    GHSBalanceAccount () {
      return this.totalAccounts.find(
        (balance) => balance.currency === this.$CURRENCY.GHS
      )
    },
    USDBalance () {
      return this.USDBalanceAccount?.currentBalance || 0
    },
    NGNBalance () {
      return this.NGNBalanceAccount?.currentBalance || 0
    },
    GHSBalance () {
      return this.GHSBalanceAccount?.currentBalance || 0
    },
    balanceReport () {
      return this.$store.state.report?.dashboardReports || {}
    },
    totalAmountReceived () {
      return (
        this.$store.state.report?.dashboardReports.totalAmountReceived || {}
      )
    },
    ongoingTransfersCount () {
      return (
        this.$store.state.report.dashboardReports.ongoingTransfersCount || 0
      )
    },
    ongoingDepositCount () {
      return (
        this.$store.state.report?.dashboardReports?.ongoingDepositCount || 0
      )
    },
    ongoingBulkPayoutCount () {
      return (
        this.$store.state.report?.dashboardReports?.ongoingBulkPayoutCount || 0
      )
    },
    totalBalanceDetails () {
      if (this.tenantPermission('multiple-currency-deposit-access')) {
        if (this.USDBalanceAccount) {
          return this.USDBalanceAccount
        } else if (this.NGNBalance) {
          return this.NGNBalanceAccount
        } else if (this.filteredBalanceAccounts.length > 0) {
          return this.filteredBalanceAccounts[0]
        }
      } else {
        return this.filteredBalanceAccounts[0]
      }
    },
    filteredPaymentLinkCurrencies () {
      let filteredCurrencies = []
      const businessSupportedCurrencies = OMNI_SUPPORTED_CURRENCIES.filter(
        (currency) => {
          return this.filteredDepositAccounts.some(
            (account) => account.currency === currency.currency
          )
        }
      )
      switch (true) {
        case this.tenantIsTma:
          filteredCurrencies = businessSupportedCurrencies.filter(
            (currency) => currency.currency === this.$CURRENCY.NGN
          )
          break
        case this.tenantIsSymmetral:
          // Filter businessSupportedCurrencies based on whether they exist in SYMMETRAL_CURRENCIES
          filteredCurrencies = businessSupportedCurrencies.filter((currency) =>
            SYMMETRAL_CURRENCIES.some(
              (symmetralCurrency) =>
                symmetralCurrency.currency === currency.currency
            )
          )
          break
        case this.tenantIsIFT:
          filteredCurrencies = IFT_OMNI_SUPPORTED_CURRENCIES.filter(
            (currency) =>
              IFT_CURRENCIES.some(
                (iftCurrency) => iftCurrency.currency === currency.currency
              )
          )
          break
        case !this.isOnDevEnvironment && !this.tenantIsTma:
          filteredCurrencies = businessSupportedCurrencies.filter(
            (currency) => currency.currency === this.$CURRENCY.USD
          )
          break
        default:
          filteredCurrencies = businessSupportedCurrencies
      }
      return filteredCurrencies
    }
  }
}
